import { format } from 'date-fns';
import { graphql } from 'gatsby';
import Link from 'gatsby-link';
import { get, padStart, startCase, orderBy } from 'lodash';
import React, { Component, Fragment} from 'react';
import { A, Box, H1, H3, H4, H6, P, Small } from '@upsolve/ui';
import { legalServiceProviders } from '@upsolve/shared';

import Article from '../components/Layout/Article.div';
import AuthorsBlock from '../components/Layout/AuthorsBlock';
import BreadcrumbNav from '../components/Layout/BreadcrumbNav';
import CTASideBySideOfferings from '../components/CTA/CTASideBySideOfferings';
import ContentShareRow from '../components/Share/ContentShareRow';
import HeroFilerCta from '../components/CTA/HeroFilerCta';
import HorizontalRule from '../components/Layout/HorizontalRule.hr';
import ImageStaticMap from '../components/Media/ImageStaticMap';
import LegalServiceListing from '../components/Programmatic/LegalServiceListing';
import MainFooter from '../components/Layout/Footer'
import MainHeader from '../components/Layout/Header'
import SCREENER_CTA_TYPES from '../components/analytics/screenerCtaTypes';
import SEO from '../components/SEO/SEO';
import TopLevelLayout from '../components/Layout/TopLevelLayout';
import UpsolveFilersCount from '../components/ProofBuilders/UpsolveFilersCount';
import courts from '../../content/courts.json';
import getScreenerUrl from '../components/Links/getScreenerUrl';
import trackPageView from '../components/analytics/trackPageView';

import * as CONTENT_CREATION_METHODS from '../components/analytics/contentCreationMethods';
import * as CONTENT_GROUPS from '../components/analytics/contentGroups';
import * as CONTENT_TOPICS from '../components/analytics/contentTopics';
import * as SEARCH_TERM_TARGETS from '../components/analytics/searchTermTargets';

const contentGroup = CONTENT_GROUPS.CITY_PAGE;

export default class FreeLawyerTemplate extends Component {
  templateProps = {
    pageComponentName: 'FreeLawyerTemplate',
    pageComponentVersion: 0,
  };

  componentDidMount() {
    trackPageView({
      contentCreationMethod: CONTENT_CREATION_METHODS.PROGRAMMATIC,
      contentGroup,
      contentLocalized: true,
      contentTopics: [CONTENT_TOPICS.ATTORNEY_COST],
      searchTermTarget: SEARCH_TERM_TARGETS.HOW_TO_FILE_BANKRUPTCY,
      ...this.templateProps,
    });
  }

  render() {
    const { path } = this.props;
    const { amp, city, created, modified, state } = this.props.pageContext;
    const title = `Free Bankruptcy Attorney in ${startCase(city.city)}, ${city.state}${(city.zipcodes || []).length === 1 ? ` ${padStart(city.zipcodes[0], 5, '0')}`: ''} ${format(new Date(), 'yyyy')}`;
    const description = `${format(new Date(), 'MMM d, yyyy')} - Find a free bankruptcy attorney in ${startCase(city.city)}, ${state}. If you cannot afford an attorney and you want to file for Chapter 7 bankruptcy to erase your debt, here is a list of legal aid nonprofits that can help you at no cost.`;
    const cta = `File Bankruptcy in ${startCase(city.city)}`;
    const attorneys = get(this.props.data, 'upsolve.searchPlace.nearByAttorneys', []);
    const screenerUrl = getScreenerUrl({ cta: SCREENER_CTA_TYPES.CITY_PAGE, contentGroup });

    return (
      <TopLevelLayout showPersistentCta persistentCtaText={cta}>
        <SEO
          seoNode={{
            title,
            description,
            structuredType: 'article',
            article: {
              createdAt: created,
              updatedAt: modified,
            },
            includeRatings: true,
          }}
        />
        <MainHeader contentGroup={contentGroup} />
        <Article>
          <BreadcrumbNav />
          <div className="article__body">
            <article className="article__content">
              <div className="content-hero">
                <header className="content-hero__header">
                  <H1>Free Bankruptcy Attorney in {startCase(city.city)}, {state}{(city.zipcodes || []).length === 1 ? ` ${padStart(city.zipcodes[0], 5, '0')}`: ''}</H1>
                  <UpsolveFilersCount />
                  <HorizontalRule />
                  <AuthorsBlock
                    createdAt={created}
                    updatedAt={modified}
                  />
                </header>
              </div>
              <div className="content-body">
                <div className="content-body__container">
                  <nav>
                    <H6>Read on to learn more about:</H6>
                    <ul className="content-body__nav">
                      <li><a href="#getting-guidance">Getting Guidance</a></li>
                      <li><a href="#cant-afford-lawyer">What Can I Do If I Can’t Afford a Bankruptcy Lawyer?</a></li>
                      <li><a href="#legal-aid">Getting Legal Aid Bankruptcy Help</a></li>
                      <li><a href="#file-on-my-own">File on Your Own for Free with Upsolve</a></li>
                      <li><a href="#searching-elsewhere">Where Else Can I Look?</a></li>
                    </ul>
                  </nav>
                  <section>
                    <p>So, you’ve decided to get a fresh start by <a href="/learn/pros-cons-filing-chapter-7-bankruptcy/">filing for Chapter 7 bankruptcy</a>.</p>
                    <p>This is a huge step toward a stronger financial future.</p>
                    <p>Chapter 7 bankruptcy is an important <a href="/learn/should-i-file-for-bankruptcy-or-debt-relief/">debt relief tool</a> for Americans in severe financial distress from losing a job, getting injured, or getting divorced.</p>
                    <p>Hiring a good bankruptcy attorney like a member of the <a href="https://www.nacba.org" target="_blank" rel="noopener noreferrer">NACBA</a> is a great investment. They will be available to answer your questions and help take care of the Chapter 7 paperwork for an average of $1500.</p>
                    <p>The problem is that many {startCase(city.city)} <a href="https://www.propublica.org/series/too-broke-for-bankruptcy" target="_blank" rel="noopener noreferrer">residents looking to file for bankruptcy but can’t afford that rate</a>.</p>
                    <p>If you need a fresh start in {startCase(city.city)} and cannot afford a lawyer, you’re not alone.</p>
                    <p>We’ll walk you through where to look and what “brick and mortar” legal aid resources are available in {startCase(city.city)} {city.state} to help you get debt free.  You’ll also learn about our digital legal aid nonprofit, Upsolve.org, that helps low income Americans who need a fresh start for free.</p>
                    <p>Whether or not you choose to file Chapter 7 using our free nonprofit <Link to="/">Upsolve</Link>, this article will leave you much better prepared to obtain a better financial future.</p>
                  </section>
                  <section>
                    <H3 as='h2' id="getting-guidance">Get Guidance</H3>
                    <p><a href="/learn/how-to-file-bankruptcy-2019/">Filing on your own</a> doesn’t mean that you should go through the bankruptcy process by yourself. It is important that you still have some guidance or supervision.</p>
                    <p>There is a lot on the line when you’re filing for bankruptcy. If done incorrectly you could jeopardize your chances of  getting out of debt and keeping your property.</p>
                    <p>If this filing doesn’t go as planned, you might have to <a href="/learn/how-often-can-you-file-bankruptcy/">wait 8 years</a> before you’re able to file Chapter 7 again.</p>
                    <p>All things considered, you want to make sure this filing is done right by getting some assistance.</p>
                  </section>
                  <section>
                    <H3 as='h2' id="cant-afford-lawyer">What Can I Do If I Can’t Afford a Bankruptcy Lawyer?</H3>
                    <p>If you <Link to="/learn/should-i-file-for-chapter-7-bankruptcy/">think you should file for bankruptcy</Link> but can’t afford an attorney, you still have options.</p>
                    <p>There are nonprofit and legal aid organizations around {startCase(city.city)} that are able to provide free legal help to {city.state} residents.</p>
                    <p>These organizations may have different requirements to use their service. It’s important that you meet the requirements.</p>
                    <p>If you’re eligible, they can usually provide free legal representation or assist you in filing on your own.</p>
                    <p>Locating the service that can best help you is the first step to getting a fresh start.</p>
                  </section>
                  <section>
                    <H3 as='h2' id="legal-aid">Get Bankruptcy Help from a Legal Aid Organization</H3>
                    <H4 as="h3">What is Legal Aid?</H4>
                    <p>Navigating the legal system can be frustrating and complicated, especially if you’ve never done it before.</p>
                    <p>Legal aid organizations help low-income people get the legal help they need but often can’t afford.</p>
                    <p>These organizations are typically funded by grants from philanthropists, like the <a href="https://kresge.org/" target="_blank" rel="noopener noreferrer">Kresge Foundation</a>,  and government agencies such as the <a href="https://www.lsc.gov/" target="_blank" rel="noopener noreferrer">Legal Services Corporation</a>.</p>
                    <p>Legal aid organizations  and are in place to address a variety of legal problems under one roof. These often include: tenant-landlord issues, debt collection lawsuits, domestic abuse cases, immigration, etc.</p>
                    <p>If your local legal aid organization can’t help you with your specific bankruptcy issue, they can probably refer you to another organization or service in the {startCase(city.city)}, {city.state} area that can.</p>
                    <p>It’s not uncommon for these organizations to have waiting lists, but sometimes they do not. When you do reach out, you may be put on hold or need to give them a couple of calls.</p>
                    <p>Keep in mind that the time you put into figuring out if you qualify may save you thousands of dollars.</p>
                    <p>If you’re wondering whether your local legal aid organization will take your case, the best thing you can do is give them a call and ask.</p>
                    <p>Below are some of the guidelines that legal aids use to see if you’re eligible.</p>
                    <H4 as="h3">See If You’re Eligible</H4>
                    <p>If you think you may qualify for free legal aid help, you should call your local legal aid organization rather than guess whether you qualify for free help or not.</p>
                    <p>Most legal aid organizations require you to be <a href="https://www.federalregister.gov/documents/2018/01/23/2018-01138/income-level-for-individuals-eligible-for-assistance" target="_blank" rel="noopener noreferrer">income eligible</a>. This means that they can only assist you if you make less than a certain amount of money.</p>
                    <p>Although it varies, most legal aids help people below 200% of the poverty line or below 125% of the poverty line.</p>
                    <ul>
                      <li>At 200%, that is about $24,280 for an individual or $50,200 for a family of four.</li>
                      <li>At 125%, that is about $15,175/year for an individual and $31,375/year for a family of four.</li>
                    </ul>
                    <H4 as="h3">Meet the Other Requirements</H4>
                    <p>Sometimes there are other requirements in addition to how much you make.</p>
                    <p>These requirements may be that you:</p>
                    <ul>
                      <li>are a United States citizen</li>
                      <li>do not own over a certain amount of assets</li>
                      <li>have a certain amount of debt</li>
                    </ul>
                    <H4 as="h3">Exceptions to the Legal Aid Requirements</H4>
                    <p>It’s possible that you can be eligible to get help from legal aid even if you don’t meet any of the above requirements.</p>
                    <p>This usually happens if you’re a <Link to="/learn/how-to-get-free-legal-aid-help/">member of a certain group of people</Link>. For example, legal aid often have specific services for military <a href="https://www.va.gov/ogc/legalservices.asp" target="_blank" rel="noopener noreferrer">veterans</a>, senior citizens, or if you are living with certain medical conditions.</p>
                    <p>The best way to find out whether you can get help is to just call  and speak to someone directly.</p>
                    <H4 as="h3">What is it Like Working With Legal Aid</H4>
                    <p>If you call a legal aid organization, you can expect a phone operator or paralegal to first ask questions to determine whether you qualify for their free assistance and to see if you’re a good fit for bankruptcy.</p>
                    <p>Different legal aid organizations collect this information in different ways.</p>
                    <p>Sometimes it will entail asking you to come into their office for an interview. Sometimes it will entail filling out a paper or online questionnaire.</p>
                    <p>As you complete this questionnaire, it is important, once again, to remain totally honest. The information you provide will go onto your bankruptcy forms that are submitted to the court.</p>
                    <p>If your legal aid organization provides help in a limited assistance capacity, like nonprofits such as <Link to="/">Upsolve</Link> do, then you’ll be responsible for filing the actual bankruptcy forms on your own and showing up by yourself to the 341 meeting.</p>
                    <H4 as="h3">Things to Keep in Mind When Considering Legal Aid</H4>
                    <p>Getting help from a legal aid organization is a great option if you need guidance but can’t afford an attorney.</p>
                    <p>That said, there are some things that might impact your experience with legal aid.</p>
                    <p>It’s important to keep in mind that, although they offer assistance for a variety of legal needs, many legal aid organizations don’t do bankruptcy. And, if they do, there might be a waiting list to receive assistance.</p>
                    <p>Because legal aid organizations offer face-to-face contact with a lawyer, it takes longer for them to help each client. Because of this, it’s important to know what your timeline looks like and how long you are able to wait before getting relief.</p>
                    <p>Even if legal aid does offer bankruptcy assistance, as we mentioned above, you would still need to qualify for help based on the criteria mentioned above.</p>
                    <p>These limits in legal aid’s ability to provide service to people in need  sometimes complicates things if you need straightforward bankruptcy help.</p>
                  </section>
                  <section>
                    <H3 as='h2' id="file-on-my-own">File on Your Own with “Digital Legal Aid” Upsolve</H3>
                    <p>Upsolve is a technology company committed to helping people achieve financial empowerment.</p>
                    <H4 as="h3">How Upsolve Works</H4>
                    <p>If brick and mortar legal aid doesn’t work, consider digital legal aid Upsolve.</p>
                    <p>Our nonprofit <Link to="/">Upsolve</Link> was founded in Harvard Law’s Access to Justice Lab to help low-income Americans who need a fresh start. We know that the people who would benefit most from bankruptcy can’t afford the help they deserve.</p>
                    <p>Upsolve is a nonprofit dedicated to giving low-income Americans a fresh start. Our software helps people across the country complete the bankruptcy forms from their computer, tablet, or smartphone.</p>
                    <p>Here’s how it works:</p>
                    <p>Like legal aid, Upsolve will first ask you a few questions to make sure you’re eligible and that filing makes sense.</p>
                    <p>This is where the process changes...</p>
                    <p>You’ll then create an account at <a href={screenerUrl}>Upsolve.org</a> and take a 1-hr questionnaire about everything you make, spend, owe, and own.</p>
                    <p>Our software then generates a draft of the required bankruptcy forms, which are reviewed by a Upsolve Team Member for completeness and consistency and then sent to you to review before you file on your own.</p>
                    <p>You’ll also be able to take pictures any other documents you need for your case.</p>
                    <p>Using Upsolve is free. And, because it’s an online website, we can help as many people as possible without a wait.</p>
                    <p>You’ll only be responsible for paying for the government required counseling course ($14.95) and any filing fees (if those aren’t waived for you).</p>
                    <p>After you file your paperwork, you’ll wait to attend your 341-meeting.</p>
                    <p>Upsolve will provide you information about what happens during the meeting and tips about how to be prepared.</p>
                    <p>From there, you’ll wait to get your fresh start!</p>
                  </section>
                  <section>
                    <H3 as='h2' id="searching-elsewhere">Where Do I Look for More Help?</H3>
                    <H4 as="h3">Online Searches</H4>
                    <p>The fact that you’re here means that you’ve probably already googled: “free bankruptcy help.”</p>
                    <p>Different websites and advertisements might pop up.</p>
                    <p>The good news is that you can usually find a lot of different bankruptcy attorneys in the {startCase(city.city)}, {city.state} area, but the problem is that they all cost hundreds of dollars.</p>
                    <H4 as="h3">Legal Aid</H4>
                    <p>Sometimes it is easiest to search for general legal aid in the {startCase(city.city)}, {city.state} area that offer more than just bankruptcy. Searching for places that offer different types of legal assistance might make it easier to locate legal help near you.</p>
                    <p>The contact information for the legal aid organizations closest to you are:</p>
                    {Array.prototype.map.call(legalServiceProviders.filter(lsp => lsp.state === city.state), (la) => (
                      <div key={la.name}>
                        <p>
                          <a href={la.url || ''} target="_blank" rel="noopener noreferrer">{la.name}</a><br />
                          {la.phone}<br />
                          {la.address}
                          <ImageStaticMap alt={`${la.name} Location`} address={la.address} />
                        </p>
                      </div>
                    ))}
                    <H4 as="h3">Bankruptcy Courts</H4>
                    <p>If you’re not having any luck online, another option would be to reach out to your local bankruptcy court.</p>
                    <p>The bankruptcy court is a central location where people with bankruptcy needs and organizations with bankruptcy services can get connected.</p>
                    <div>
                      <p>Here in the {startCase(city.city)}, {city.state} area, it might make sense to reach out to these courts. You can call and ask to speak to one of the clerks or another court staff member.</p>
                      <p>The court is open from Monday through Friday excluding federal holidays during normal business hours.</p>
                      {Array.prototype.map.call(courts.filter(court => get(court, 'address.state') === city.state), (court) => (
                        <div key={court.name}>
                          <p>
                            <a href={court.url}>{court.name}</a><br />
                            {court.phone}<br />
                            {get(court, 'address.display')}
                          </p>
                          <ImageStaticMap alt={court.name} address={court.address} />
                        </div>
                      ))}
                    </div>
                    <H4 as="h3">Consultations with Bankruptcy Attorneys</H4>
                    <P>If you're still feeling unsure about your case and circumstances, it may be worth taking advantage of free consultations many lawyers offer.</P>
                  </section>
                  <section>
                    <H3 as='h2' id="conclusion">Conclusion</H3>
                    <p>Bankruptcy is one of the most important safety nets that helps people get back on their feet.</p>
                    <p>Unfortunately, many people get overwhelmed by the process and fear having to go through it alone. Without the guidance they need, many people who try to file on their own are  unsuccessful.  </p>
                    <p>Having an attorney help you is an ideal option. But, one that most people can’t afford to pursue.</p>
                    <p>If you’re eligible for their help, legal aid is a great option to explore.</p>
                    <p>Upsolve is a nonprofit dedicated to helping low-income Americans navigate Chapter 7 bankruptcy when you can’t afford to hire an attorney.</p>
                    <p>Upsolve wants to make bankruptcy understandable for the people who need it most. We help make sense of the process and get you the information you need to head toward a fresh start.</p>
                  </section>
                </div>
                <aside className="article__sidebar">
                  <div className="sidebar-hero__cta">
                    <HeroFilerCta contentGroup={contentGroup} />
                  </div>
                </aside>
              </div>
            </article>
          </div>
          <div className="related-content">
            <ContentShareRow text={title} slug={path} />
          </div>
        </Article>
        <CTASideBySideOfferings contentGroup={contentGroup} ctaText={cta} />
        <MainFooter />
      </TopLevelLayout>
    );
  }
}


export const pageQuery = graphql`
  query CityFreeLawyerAttorneys($searchPlaceId: Int) {
    upsolve {
      searchPlace(idEq: $searchPlaceId) {
        nearByAttorneys(verifiedEq:true limit:15) {
          id
          verified
          businessName
          firstName
          lastName
          formattedSlug
          logo {
            url
          }
          address {
            street1
            street2
            street3
            street4
            city
            state
            zipcode
            formatted
            latitude
            longitude
          }
          biography
          phoneNumber
          languages {
            language
            proficiency
          }
          jurisdictions {
            year
            state
          }
          websites {
            name
            url
          }
          contentScore
        }
      }
    }
  }
`;
